/**
 * Browser-side signature ink measurement (PF-6119).
 *
 * The advisory half of a deliberate pair: this drives the submit button live, and
 * `submitSignatureRecapture` re-measures with `sharp` on the server to decide. Both must produce
 * identical `SignatureInkMetrics` for the same input — if they drift near a threshold the patient
 * signs, the button enables, and the server rejects with nothing they can act on. Both constants
 * below are part of that contract and must match the server module.
 *
 * `SignatureInput` (`react-signature-canvas`) does not expose its live canvas element through
 * `SignatureInputHandle`, so this decodes the same base64 PNG the pad already emits via `onChange`
 * into an offscreen canvas and measures that — same pixels, same four metrics.
 */
import type { SignatureInkMetrics } from '@physiofit-digital/physiofit_types';

/**
 * A pixel counts as ink only if it is BOTH opaque enough and dark enough. Both tests are required,
 * because the two pads this gate has to measure export different things:
 *
 * - The patient app's `react-native-signature-canvas` exports a TRANSPARENT PNG — background alpha
 *   0, strokes opaque. Alpha alone separates them, which is what the original analysis of the three
 *   rejected assets relied on.
 * - This repo's `SignatureInput` sets `backgroundColor={colors.white}`, so its PNG is fully OPAQUE.
 *   Alpha is 255 on every pixel, and an alpha-only test counts the entire canvas as ink — every
 *   signature, including a single tap, then measures `inkRatio: 1` and passes. The gate would be
 *   decorative.
 *
 * Darkness alone is not enough either: a transparent PNG's untouched pixels are (0,0,0,0), which is
 * black, so they would all count. Hence the AND.
 *
 * The pen is `colors.dark` (#1e3542, luminance ~47) on white (255), so 200 sits far from both and
 * still catches anti-aliased stroke edges.
 */
const ALPHA_THRESHOLD = 10;
const LUMINANCE_THRESHOLD = 200;

/** Rec. 601 luma, the same weighting `sharp` and every image tool uses for greyscale. */
const luminance = (r: number, g: number, b: number): number => 0.299 * r + 0.587 * g + 0.114 * b;

function loadImageData(dataUrl: string): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('2D canvas context unavailable'));
        return;
      }
      ctx.drawImage(image, 0, 0);
      resolve(ctx.getImageData(0, 0, canvas.width, canvas.height));
    };
    image.onerror = () => reject(new Error('Failed to decode signature image'));
    image.src = dataUrl;
  });
}

/**
 * Measures how much ink a signature PNG (data URL, as `SignatureInput` emits it) actually carries.
 *
 * @param {string} dataUrl `data:image/png;base64,…` from the signature pad.
 * @return {Promise<SignatureInkMetrics>} Pixel counts and bounding-box spans.
 */
export const measureInk = async (dataUrl: string): Promise<SignatureInkMetrics> => {
  const { width, height, data } = await loadImageData(dataUrl);

  let inkPixels = 0;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      const alpha = data[offset + 3];
      if (alpha <= ALPHA_THRESHOLD) {
        continue;
      }
      if (luminance(data[offset], data[offset + 1], data[offset + 2]) >= LUMINANCE_THRESHOLD) {
        continue;
      }
      inkPixels += 1;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }

  const hasInk = maxX >= 0;
  return {
    width,
    height,
    inkPixels,
    inkRatio: inkPixels / (width * height),
    boundingBoxWidthRatio: hasInk ? (maxX - minX + 1) / width : 0,
    boundingBoxHeightRatio: hasInk ? (maxY - minY + 1) / height : 0,
  };
};
